/**
 * INVOICE-SENT-01 — "Invoices To Send" on Today (Seth 2026-09-17: "anything
 * not sent I'd want the office to know so they can review and send invoice").
 *
 * Issued Pegasus invoices from the last 30 days with money on them that no
 * one has emailed to the customer (never queued, or the last try failed /
 * the customer opted out of email). Each row's Review & send opens the
 * invoice with the email window already up — the office sees the document,
 * fixes the address if the tech left it blank, sends. Renders nothing on a
 * clean day, so the Billing section hides itself.
 */
import { ArrowRight, Receipt } from "lucide-react";
import { useNavigateWithBack } from "@/lib/navBack";
import { useUnsentBillableInvoices, InvoiceSentChip } from "@/features/comms/invoiceSend";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const SHOW = 8;

export function UnsentInvoicesPanel() {
  const navigate = useNavigateWithBack();
  const { data: rows } = useUnsentBillableInvoices(30);
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="rounded-2xl border border-hairline bg-white p-4 shadow-sm">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900">
          {rows.length} invoice{rows.length === 1 ? "" : "s"} not sent to the customer
          <span className="ml-2 font-normal text-slate-500">{money(total)} billed · last 30 days</span>
        </p>
        <button
          type="button"
          onClick={() => navigate("/invoices?filter=unsent")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
        >
          Open the list <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <ul className="divide-y divide-hairline/70">
        {rows.slice(0, SHOW).map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
            <Receipt className="h-4 w-4 shrink-0 text-slate-300" />
            <span className="w-20 shrink-0 tabular-nums text-xs text-slate-500">{r.date ?? "—"}</span>
            <button
              type="button"
              onClick={() => r.custno && navigate(`/customers/${r.custno}?tab=invoices`)}
              className="min-w-0 max-w-[220px] truncate text-left font-medium text-slate-900 hover:text-brand-700"
              title="Open the customer"
            >
              {r.customerName ?? r.custno ?? "—"}
            </button>
            <span className="font-mono text-xs text-slate-500">#{r.invoiceNo}</span>
            {r.technician && <span className="text-xs text-slate-400">{r.technician}</span>}
            <span className="ml-auto shrink-0 font-semibold tabular-nums text-slate-800">{money(r.total)}</span>
            <InvoiceSentChip
              size="xs"
              status={r.status}
              sendLabel="Review & send"
              onSend={() => navigate(`/invoices/${r.id}?email=1`)}
            />
          </li>
        ))}
      </ul>
      {rows.length > SHOW && (
        <p className="mt-2 text-xs text-slate-400">+ {rows.length - SHOW} more on the Invoices list.</p>
      )}
    </div>
  );
}
